import { useState, useEffect } from 'react'
import { useWizardStore } from '../../store/wizardStore'
import { Input } from '../ui/Input'
import { Button } from '../ui/Button'
import { CardHeader, CardTitle, CardContent } from '../ui/Card'
import { api } from '../../lib/api'
import { formatDateUK, addDays } from '../../lib/dateUtils'

export default function Step1UserDetails() {
  const { email, fullName, phoneNumber, setUserDetails, setOrderId, nextStep } = useWizardStore()
  const [localEmail, setLocalEmail] = useState(email)
  const [localFullName, setLocalFullName] = useState(fullName)
  const [localPhoneNumber, setLocalPhoneNumber] = useState(phoneNumber)
  const [errors, setErrors] = useState<{ email?: string; fullName?: string }>({})
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [fayreDate, setFayreDate] = useState<string>('')

  useEffect(() => {
    async function loadEventDate() {
      try {
        const { eventDate } = await api.getEventDate()
        if (eventDate) {
          setFayreDate(eventDate)
        }
      } catch (err) {
        console.error('Failed to load event date:', err)
      }
    }
    loadEventDate()
  }, [])

  const validateEmail = (email: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return re.test(email)
  }

  const handleNext = async () => {
    const newErrors: { email?: string; fullName?: string } = {}

    if (!localEmail.trim()) {
      newErrors.email = 'Email is required'
    } else if (!validateEmail(localEmail)) {
      newErrors.email = 'Please enter a valid email address'
    }

    if (!localFullName.trim()) {
      newErrors.fullName = 'Full name is required'
    }

    setErrors(newErrors)
    setSubmitError(null)

    if (Object.keys(newErrors).length === 0) {
      setIsLoading(true)
      try {
        // Create order in database
        const order = await api.createOrder({
          email: localEmail,
          fullName: localFullName,
          phoneNumber: localPhoneNumber,
          wantsGifts: false, // Will be updated in next step
        })

        setUserDetails(localEmail, localFullName, localPhoneNumber)
        setOrderId(order.id)
        nextStep()
      } catch (error) {
        console.error('Failed to create order:', error)
        setSubmitError("We couldn't start your order. Please check your connection and try again.")
      } finally {
        setIsLoading(false)
      }
    }
  }

  return (
    <>
      <CardHeader>
        <CardTitle>Welcome to the Elves' Workshop!</CardTitle>
        <p className="text-gray-600 mt-2">
          The Elves' Workshop allows your child/children to choose gifts for family, friends or pets from a selection we have available. We will then wrap their gift, and at the Christmas Fayre they may decorate their wrapped gifts in the workshop.
        </p>
      </CardHeader>
      <div className="mb-6 border-y-2 border-cream divide-y-2 divide-cream">
        <div className="flex items-start gap-4 py-4">
          {fayreDate && <DateTile isoDate={fayreDate} tone="amber" />}
          <div>
            <h3 className="text-lg font-bold text-gray-900">
              Christmas Fayre{fayreDate && <span className="block">{formatDateUK(fayreDate)}</span>}
            </h3>
            <p className="text-gray-900 font-semibold mt-1">3.45pm, after school, in Upper School</p>
            <p className="text-gray-600 text-sm mt-1">Please enter by the main entrance and sign in.</p>
          </div>
        </div>
        <div className="flex items-start gap-4 py-4">
          {fayreDate && <DateTile isoDate={addDays(fayreDate, 7)} tone="sage" />}
          <div>
            <h3 className="text-lg font-bold text-gray-900">
              Can't make the fayre?
            </h3>
            <p className="text-gray-600 mt-1">
              Gifts will be given out at school{fayreDate ? <> on <strong className="font-semibold text-gray-900">{formatDateUK(addDays(fayreDate, 7))}</strong></> : ''}.
            </p>
          </div>
        </div>
      </div>
      <CardContent>
        <div className="space-y-4">
          <Input
            label="Email Address"
            type="email"
            autoComplete="email"
            placeholder="your.email@example.com"
            value={localEmail}
            onChange={(e) => setLocalEmail(e.target.value)}
            error={errors.email}
            required
          />
          <Input
            label="Your Full Name (adult making the booking)"
            type="text"
            autoComplete="name"
            placeholder="John Smith"
            value={localFullName}
            onChange={(e) => setLocalFullName(e.target.value)}
            error={errors.fullName}
            required
          />
          <Input
            label="Phone Number (optional)"
            type="tel"
            autoComplete="tel"
            placeholder="07123 456789"
            value={localPhoneNumber}
            onChange={(e) => setLocalPhoneNumber(e.target.value)}
          />
          {submitError && (
            <div role="alert" className="bg-primary-50 border border-primary-300 p-4 rounded-lg">
              <p className="text-primary-800">{submitError}</p>
            </div>
          )}
          <div className="pt-4">
            <Button
              onClick={handleNext}
              fullWidth
              size="lg"
              disabled={isLoading}
            >
              {isLoading ? 'Loading...' : 'Continue'}
            </Button>
          </div>
        </div>
      </CardContent>
    </>
  )
}

// Tear-off calendar page: short month on a coloured strip, big day number below.
function DateTile({ isoDate, tone }: { isoDate: string; tone: 'amber' | 'sage' }) {
  const date = new Date(isoDate + 'T00:00:00')
  return (
    <div
      aria-hidden="true"
      className="w-16 flex-shrink-0 overflow-hidden rounded-lg border-2 border-gray-200 bg-white text-center shadow-sm"
    >
      <div className={tone === 'amber' ? 'bg-accent-500 py-0.5' : 'bg-secondary-300 py-0.5'}>
        <span className="text-xs font-bold uppercase tracking-wide text-secondary-foreground">
          {date.toLocaleDateString('en-GB', { month: 'short' })}
        </span>
      </div>
      <div className="py-1 text-2xl font-bold leading-tight tabular-nums text-secondary-foreground">{date.getDate()}</div>
      <div className="pb-1 text-xs font-medium text-gray-600">
        {date.toLocaleDateString('en-GB', { weekday: 'short' })}
      </div>
    </div>
  )
}
